'use client';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import { ReadonlyURLSearchParams } from 'next/dist/client/components/navigation.react-server';
import React, { createContext } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import Cookies from 'js-cookie';
import { GeoLang, CurrentUrlPropsV2 } from '../types/';

const { base } = publicRuntimeConfig;

export const CurrentUrlContext = createContext<CurrentUrlPropsV2>({
  geo: {
    geoLoc: '',
    country: '',
    locale: '',
    generalPage: '',
    productGeo: '',
    errorPage: '',
    flag: '',
    name: '',
    currency: '',
  },
  country: '',
  path: '',
  productGeo: '',
  locale: '',
  deviceType: '',
  currency: 'USD',
  host: '',
  home: '/',
  base: '',
  eCommEnabled: false,
  accountsEnabled: true,
  enableBazaarvoice: true,
  query: new ReadonlyURLSearchParams(),
  push: () => Promise.resolve(false),
  replace: () => Promise.resolve(false),
  refresh: () => Promise.resolve(false),
});

interface ProviderProps {
  pageGeo?: GeoLang;
  geoLangs?: Array<GeoLang>;
  children: React.ReactNode;
  getGeoFunc?: (path: string, geos: Array<GeoLang>) => GeoLang;
}

const getCurrentUrl = (
  router: ReturnType<typeof useRouter>,
  pathname: string,
  searchParams: ReadonlyURLSearchParams,
  geo: GeoLang | undefined,
): CurrentUrlPropsV2 => {
  if (!geo) {
    return {
      ...router,
      path: pathname,
    } as unknown as CurrentUrlPropsV2;
  }
  const user = JSON.parse(Cookies.get('tg-usr') ?? '{}');
  const localePrefix = !geo.isDefault && !pathname?.includes(geo.geoLoc) ? geo.geoLoc : '';
  const regex = new RegExp(`(${geo.geoLoc})?/~p/[0-9]+`);
  const unprefixedPath =
    pathname !== geo.geoLoc
      ? // remove pagination rewrites from path
        (pathname?.replace(regex, geo.isDefault ? '' : geo.geoLoc) ?? '')
      : '';
  const path = `${localePrefix}${unprefixedPath}`;

  return {
    ...router,
    geo,
    country: geo.geoLoc,
    path,
    productGeo: geo.productGeo,
    locale: geo.locale,
    host: user.host,
    deviceType: user.deviceType,
    currency: geo.currency,
    base,
    home: geo.isDefault ? '/' : geo.geoLoc,
    eCommEnabled: !!geo.eCommEnabled,
    accountsEnabled: !!geo.accountsEnabled,
    enableBazaarvoice: !!geo.enableBazaarvoice,
    query: searchParams,
  };
};

const CurrentUrlProvider = ({ children, pageGeo }: ProviderProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  return (
    <CurrentUrlContext.Provider value={getCurrentUrl(router, pathname, searchParams, pageGeo)}>
      {children}
    </CurrentUrlContext.Provider>
  );
};

export default CurrentUrlProvider;
